import {
  fetchAuthSession,
  signIn as awsSignIn,
  signOut as awsSignOut,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { Observable, catchError, from, map, of, switchMap, tap, throwError } from 'rxjs';
import { Pub, Service, Sub } from '../service.abstract.ts';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { storageHelper } from '../../helpers/storage/index.ts';
import { clearRecordedLoginUserIds } from '../userActivity/loginRecordSession.ts';

export enum Auth {
  loggedIn = 'loggedIn',
  unassigned = 'unassigned',
  userNotFound = 'userNotFound',
  credentialsInvalid = 'credentialsInvalid',
  credentialsEmpty = 'credentialsEmpty',
}
export enum USER_ROLES {
  ADMIN = 'admin',
  NOT_DEFINED = 'no',
  CUSTOM_ACCESS = 'custom',
}
export enum APP_MODULES {
  CUSTOMER = '1',
  ARTICLE = '2',
  OFFER = '10',
  ORDER = '3',
  PRODUCTION = '4',
  INVOICE = '5',
  DEBITOR = '6',
  STATISTIC = '9',
  OPTIONS = '7',
  GLOBAL_SEARCH = '9999', // we don't have this module in the AWS congnito, but we need it always open
}
type Actions = 'signIn' | 'signOut';

let sessionExpirationInFlight: Promise<void> | null = null;

function isExpiredAuthSessionError(error: unknown): boolean {
  const { name = '', message = '' } = error as { name?: string; message?: string };

  if (name === 'UserUnAuthenticatedException' || name === 'UserUnauthenticatedException') {
    return true;
  }

  const normalizedMessage = `${name} ${message}`.toLowerCase();
  return [
    'refresh token has expired',
    'invalid refresh token',
    'session expired',
    'no current user',
    'not authenticated',
    'user needs to be authenticated',
  ].some((phrase) => normalizedMessage.includes(phrase));
}

function registerPostHogCompany(companyGuid: string): void {
  if (!posthog.__loaded) return;
  try {
    posthog.register({ company: companyGuid });
  } catch {
    // Analytics is best-effort; a PostHog fault must never break the auth flow.
  }
}

function resetPostHog(): void {
  if (!posthog.__loaded) return;
  try {
    posthog.reset();
  } catch {
    // Analytics is best-effort; a PostHog fault must never break sign-out.
  }
}

function expireSessionAndRedirectToLogin(): Promise<void> {
  if (sessionExpirationInFlight) return sessionExpirationInFlight;

  sessionExpirationInFlight = (async () => {
    storageHelper.session.removeItem('userAccessParams');
    storageHelper.session.removeItem('alternativeGuid');
    clearRecordedLoginUserIds();
    resetPostHog();

    try {
      await awsSignOut();
    } catch {
      // The session is already considered expired; redirect is more important
      // than surfacing a best-effort Cognito sign-out failure.
    }

    if (window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  })().finally(() => {
    queueMicrotask(() => {
      sessionExpirationInFlight = null;
    });
  });

  return sessionExpirationInFlight;
}

/**
 * Failure ceiling for Cognito session reads (WA-3659, and the unfixed WA-3559).
 *
 * `fetchAuthSession()` also resolves AWS credentials whenever an
 * `identityPoolId` is configured, and Amplify caches only SUCCESSFUL results —
 * so a session it cannot recover is retried by every caller, indefinitely.
 * With `gqlClient` alone asking twice per request (`idToken` + `customerGUID`),
 * one broken session turned into tens of thousands of `cognito-identity` calls
 * against a pool shared with other products, until the browser ran out of
 * sockets and the app never left its splash screen.
 *
 * Every session read in this app goes through `resolveAuthSession`, so this
 * ceiling holds no matter which caller trips the failure first.
 */
const SESSION_FAILURE_LIMIT = 3;
const SESSION_FAILURE_WINDOW_MS = 10_000;

let recentSessionFailures: number[] = [];
let sessionReadsBlocked = false;

function resetSessionFailures(): void {
  recentSessionFailures = [];
  sessionReadsBlocked = false;
}

/**
 * On the login screen there is no session yet, so read failures are expected
 * and must not trip the breaker: `expireSessionAndRedirectToLogin` deliberately
 * skips the redirect when already on `/login`, so nothing would reload the page
 * and clear the tripped state — the sign-in that follows would stay blocked.
 */
function isOnLoginScreen(): boolean {
  return window.location.pathname === '/login';
}

function recordSessionFailure(): void {
  // Calls already in flight when the breaker tripped land here afterwards.
  // Without this guard they keep growing the window and re-entering the expiry
  // path — whose single-flight slot is released on a microtask, so a late
  // arrival would start a second signOut + redirect.
  if (sessionReadsBlocked) return;
  if (isOnLoginScreen()) return;

  const now = Date.now();
  recentSessionFailures = recentSessionFailures.filter(
    (at) => now - at < SESSION_FAILURE_WINDOW_MS,
  );
  recentSessionFailures.push(now);

  if (recentSessionFailures.length < SESSION_FAILURE_LIMIT) return;

  // Block reads BEFORE ending the session: `expireSessionAndRedirectToLogin`
  // awaits a Cognito sign-out, and callers keep arriving during that await.
  sessionReadsBlocked = true;
  void expireSessionAndRedirectToLogin();
}

/**
 * Single entry point for reading the Cognito session.
 *
 * Behaves exactly like `fetchAuthSession` until the ceiling above is reached;
 * from then on it answers with an EMPTY session, immediately and without a
 * network call, so an unrecoverable session costs one bounded round of requests
 * instead of a storm. A successful read clears the counter.
 *
 * Empty rather than thrown on purpose. "No session" is a state every caller
 * here already handles, and it routes the failure into the quiet path the app
 * already has: `idToken`/`refreshIdToken` resolve to `''`, so `gqlClient` fails
 * with its own `Session expired` error, which it marks as expected before
 * redirecting (WA-3514…WA-3538 — an unmarked auth error reaches Sentry, and
 * Sentry opens Jira tickets for it). A bespoke error would instead need every
 * one of those paths to learn to recognise it.
 */
async function resolveAuthSession(
  options?: Parameters<typeof fetchAuthSession>[0],
): Promise<Awaited<ReturnType<typeof fetchAuthSession>>> {
  if (sessionReadsBlocked) return {};

  try {
    const session = await fetchAuthSession(options);
    if (recentSessionFailures.length > 0 || sessionReadsBlocked) resetSessionFailures();
    return session;
  } catch (error) {
    recordSessionFailure();
    throw error;
  }
}

class PubImpl extends Pub<Actions> {
  signIn(credentials: ICredentials): void {
    this.emit('signIn', credentials);
  }
  signOut(): void {
    this.emit('signOut');
  }
}

class SubImpl extends Sub<Actions> {
  idToken(): Observable<string> {
    return from(resolveAuthSession()).pipe(
      map((session) => session.tokens?.idToken?.toString() ?? ''),
    );
  }
  /**
   * Force-refresh the Cognito session and return the fresh idToken.
   *
   * Used by the GraphQL client to recover from 401s caused by expired
   * tokens — see WA-3514 / WA-3515. `fetchAuthSession({ forceRefresh: true })`
   * exchanges the refreshToken for a new idToken bypassing Amplify's local
   * cache; the result is `''` only if the refresh token itself is dead, in
   * which case the caller should bounce the user to the login flow.
   */
  refreshIdToken(): Observable<string> {
    return from(resolveAuthSession({ forceRefresh: true })).pipe(
      map((session) => session.tokens?.idToken?.toString() ?? ''),
      catchError((error: unknown) => {
        if (isExpiredAuthSessionError(error)) {
          return of('');
        }
        return throwError(() => error);
      }),
    );
  }
  signIn(): Observable<IAuth> {
    return this.actionListener('signIn').pipe(
      switchMap(({ params }) => {
        const { username, password } = params as ICredentials;
        // A deliberate sign-in starts a new session: clear any breaker state
        // left by the dead one, or the app would stay blocked after logging in.
        resetSessionFailures();
        return from(awsSignIn({ username, password })).pipe(
          map(() => ({ status: Auth.loggedIn })),
          catchError((error: Error) => {
            switch (error.name) {
              case 'UserNotFoundException':
                return of({ status: Auth.userNotFound });
              case 'NotAuthorizedException':
                return of({ status: Auth.credentialsInvalid });
              case 'InvalidParameterException':
                return of({ status: Auth.credentialsEmpty });
              default:
                return of({ status: Auth.unassigned });
            }
          }),
        );
      }),
    );
  }
  signOut(): Observable<any> {
    return this.actionListener('signOut').pipe(
      switchMap(() => {
        storageHelper.session.removeItem('userAccessParams');
        clearRecordedLoginUserIds();
        resetPostHog();
        return from(awsSignOut());
      }),
    );
  }
  isLoggedIn(): Observable<IAuth> {
    return from(resolveAuthSession()).pipe(
      map((session) => {
        if (!session.tokens?.idToken) {
          return { status: Auth.unassigned };
        }
        const payload = session.tokens.idToken.payload;
        const username = payload['cognito:username'] as string;
        const companyGuid = payload['custom:company'] as string | undefined;
        const userEmail = payload['email'] as string | undefined;

        Sentry.setUser({ email: userEmail, username });
        Sentry.setTag('company', companyGuid);

        if (companyGuid) {
          registerPostHogCompany(companyGuid);
        }

        return { status: Auth.loggedIn };
      }),
      catchError(() => of({ status: Auth.unassigned })),
    );
  }
  /**
   * Synchronous read of the cached access params (or null when absent).
   *
   * Lets a component seed its initial render from the already-warmed cache —
   * `ProtectedRoute` resolves `getCachedUserAccessParams()` before the layout
   * mounts, so by then this is populated. Seeding avoids a first-paint with
   * default params followed by a post-`hasAccess()` correction, which shifts
   * the admin bar / sidebar and flickers the top bar + left menu (WA-3494).
   */
  getCachedUserAccessParamsSync(): IUserAccessParams | null {
    const cachedData = storageHelper.session.getItem('userAccessParams');
    return cachedData ? (cachedData as IUserAccessParams) : null;
  }
  getCachedUserAccessParams(): Observable<IUserAccessParams> {
    const cachedData = this.getCachedUserAccessParamsSync();
    if (cachedData) {
      return of(cachedData);
    }
    return this.hasAccess();
  }
  hasAccess(): Observable<IUserAccessParams> {
    return from(resolveAuthSession({ forceRefresh: true })).pipe(
      // Force refresh user session to get updated custom:access_rights and custom:access_custom_rights claims (we will send them to the backend in headers)
      switchMap(() => from(fetchUserAttributes())),
      map((attributes) => {
        const userAccessParams = structuredClone(initUserAccessParams);
        const isAdminMode = attributes?.['custom:role'] === 'admin';
        if (attributes) {
          try {
            const { faw: initRole } = JSON.parse(attributes['custom:access_rights'] as string);
            const { faw: modulesAccessRights = [] } = JSON.parse(
              attributes['custom:access_custom_rights'] as string,
            ) as { faw: string[] };

            const roleMap: Record<string, USER_ROLES> = {
              [USER_ROLES.NOT_DEFINED]: USER_ROLES.NOT_DEFINED,
              [USER_ROLES.CUSTOM_ACCESS]: USER_ROLES.CUSTOM_ACCESS,
              [USER_ROLES.ADMIN]: USER_ROLES.ADMIN,
            };
            const role = roleMap[initRole] ?? USER_ROLES.NOT_DEFINED;

            // for USER_ROLES.NOT_DEFINED we open all modules too. Because it's a default role for new users.
            if (role === USER_ROLES.NOT_DEFINED || role === USER_ROLES.ADMIN) {
              return {
                ...initUserAccessParams,
                role,
                isAdminMode,
              };
            }

            // when role === USER_ROLES.CUSTOM_ACCESS
            modulesAccessRights.forEach((moduleAccessRight) => {
              const [moduleId, isOpen] = moduleAccessRight.split(',');
              if (moduleId in userAccessParams.modules) {
                userAccessParams.modules[moduleId as APP_MODULES] = isOpen === '1';
              }
            });
            return {
              ...userAccessParams,
              role: USER_ROLES.CUSTOM_ACCESS,
              isAdminMode,
            };
          } catch (e) {
            return { ...initUserAccessParams, isAdminMode };
          }
        }
        return userAccessParams;
      }),
      tap((userAccessParams) => {
        storageHelper.session.setItem('userAccessParams', userAccessParams);
      }),
      catchError(() => of(initUserAccessParams)),
    );
  }
  customerGUID(): Observable<string> {
    return from(resolveAuthSession()).pipe(
      map((session) => {
        const guid = storageHelper.session.getItem('alternativeGuid');
        if (guid) return guid;
        return (session.tokens?.idToken?.payload?.['custom:company'] as string) ?? '';
      }),
    );
  }
  userIdentityId(): Observable<string> {
    return from(resolveAuthSession()).pipe(map((session) => session.identityId ?? ''));
  }
}
class AuthService extends Service<Actions> {
  public pub = new PubImpl(this.stream$);
  public sub = new SubImpl(this.stream$);

  expireSession(): Promise<void> {
    return expireSessionAndRedirectToLogin();
  }
}
export const authService = new AuthService();

export const initUserAccessParams: IUserAccessParams = {
  role: USER_ROLES.NOT_DEFINED,
  modules: {
    [APP_MODULES.CUSTOMER]: true,
    [APP_MODULES.ARTICLE]: true,
    [APP_MODULES.OFFER]: true,
    [APP_MODULES.ORDER]: true,
    [APP_MODULES.PRODUCTION]: true,
    [APP_MODULES.INVOICE]: true,
    [APP_MODULES.DEBITOR]: true,
    [APP_MODULES.STATISTIC]: true,
    [APP_MODULES.OPTIONS]: true,
    [APP_MODULES.GLOBAL_SEARCH]: true,
  },
  isAdminMode: false,
};

export interface ICredentials {
  username: string;
  password: string;
  alternativeguid?: string;
}

export interface IUserAccessParams {
  role: USER_ROLES;
  modules: Record<APP_MODULES, boolean>;
  isAdminMode: boolean;
}

interface IAuth {
  status: Auth;
}
